import { useState } from 'react';
import { Star, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { ratingAPI } from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const RatingDialog = ({ open, onOpenChange, job, onRatingSubmitted }) => {
  const { user } = useAuth();
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Determine if user is rating driver or driver is rating user
  const isDriverRating = user?.role === 'driver';
  const targetName = isDriverRating ? job?.user_name : job?.driver_name;
  const targetType = isDriverRating ? 'customer' : 'driver';

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    setSubmitting(true);
    try {
      await ratingAPI.create({
        job_id: job.id,
        rating,
        comment: comment.trim() || null,
      });
      toast.success('Rating submitted! Thank you for your feedback.');
      onRatingSubmitted?.(job.id);
      onOpenChange(false);
      setRating(0);
      setComment('');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit rating';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setRating(0);
    setHoverRating(0);
    setComment('');
  };

  if (!job) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Rate Your Experience</DialogTitle>
          <DialogDescription>
            How was your experience with {targetName}?
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 pt-2">
          {/* Job Summary */}
          <div className="bg-zinc-50 rounded-lg p-4">
            <p className="font-medium text-zinc-900 capitalize">
              {job.job_type === 'ride' ? 'Standard Ride' : `${job.delivery_category} Delivery`}
            </p>
            <p className="text-sm text-zinc-600 mt-1">
              {job.pickup_address} → {job.dropoff_address}
            </p>
            <p className="text-sm text-zinc-500 mt-1">
              {isDriverRating ? 'Customer' : 'Driver'}: <span className="font-medium">{targetName}</span>
            </p>
          </div>

          {/* Star Rating */}
          <div className="text-center">
            <p className="text-sm text-zinc-600 mb-3">Tap a star to rate</p>
            <div className="flex justify-center gap-2" data-testid="rating-stars">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="p-1 transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 rounded"
                  data-testid={`rating-star-${star}`}
                >
                  <Star
                    className={`w-10 h-10 transition-colors ${
                      star <= (hoverRating || rating)
                        ? 'text-yellow-400 fill-yellow-400'
                        : 'text-zinc-300'
                    }`}
                  />
                </button>
              ))}
            </div>
            {rating > 0 && (
              <p className="text-sm text-zinc-600 mt-2">
                {rating === 5 && 'Excellent!'}
                {rating === 4 && 'Great!'}
                {rating === 3 && 'Good'}
                {rating === 2 && 'Fair'}
                {rating === 1 && 'Poor'}
              </p>
            )}
          </div>

          {/* Comment */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-zinc-700">
              Additional comments (optional)
            </label>
            <Textarea
              placeholder="Share your experience..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              data-testid="rating-comment"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleClose} 
              className="flex-1"
            >
              Skip
            </Button>
            <Button
              onClick={handleSubmit}
              className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
              disabled={rating === 0 || submitting}
              data-testid="submit-rating-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Rating'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RatingDialog;
